import React, { useEffect, useMemo, useState } from "react";
import { listSites } from "../api/sites";
import { getSiteSummary } from "../api/siteSummary";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";

export default function PerformanceReport() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        setSites(data || []);
        if (data && data.length > 0) {
          setSelectedSite(data[0].id);
        }
      } catch (err) {
        console.error("Failed to load sites", err);
      }
    };
    loadSites();
  }, []);

  const loadSummary = async (siteId) => {
    if (!siteId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSiteSummary(siteId);
      setSummary(data);
    } catch (err) {
      console.error("Failed to load site summary", err);
      setError("Could not load site summary. Ensure the backend is reachable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSite) loadSummary(selectedSite);
  }, [selectedSite]);

  const metrics = summary?.summary_metrics || {};
  const inverterUtilization = metrics.total_ac_capacity_kw
    ? (metrics.total_dc_capacity_kwp || 0) / (metrics.total_ac_capacity_kw || 1)
    : 0;
  const modulesPerArray = metrics.total_arrays ? (metrics.total_modules || 0) / metrics.total_arrays : 0;
  const signalCoverage = summary?.arrays?.length
    ? Math.min(1, (summary?.signal_matches?.length || 0) / summary.arrays.length)
    : 0;

  const arrayBreakdown = useMemo(() => {
    if (!summary) return [];
    const moduleMap = new Map((summary.pvmodules || []).map((m) => [m.id, m]));
    const arrayInverters = summary.array_inverters || [];
    const arrayModules = summary.array_modules || [];

    return (summary.arrays || []).map((array) => {
      const linked = arrayInverters.filter((inv) => inv.array_id === array.id);
      let dc = 0;
      linked.forEach((link) => {
        arrayModules
          .filter((am) => am.inv_array_id === link.id)
          .forEach((am) => {
            const module = moduleMap.get(am.module_id);
            if (module?.power_wp) {
              dc += (module.power_wp * (am.quantity || 0)) / 1000;
            }
          });
      });
      return {
        id: array.id,
        name: array.array_name || array.name || `Array ${array.id}`,
        dc,
      };
    });
  }, [summary]);

  const totalDc = arrayBreakdown.reduce((acc, arr) => acc + arr.dc, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Performance Ratio</h1>
          <p className="text-slate-600">
            A quick health view of modeled capacity versus data coverage. Use this page to verify if a site is ready for
            detailed KPI calculations.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={selectedSite || ""}
            onChange={(e) => setSelectedSite(Number(e.target.value))}
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
          <Button onClick={() => loadSummary(selectedSite)} disabled={!selectedSite || loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && <div className="text-sm text-rose-600">{error}</div>}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="DC Capacity"
          value={`${(metrics.total_dc_capacity_kwp || 0).toFixed(1)} kWp`}
          helper={`${metrics.total_inverters || 0} inverter groups`}
        />
        <KpiCard
          label="AC Capacity"
          value={`${(metrics.total_ac_capacity_kw || 0).toFixed(1)} kW`}
          helper={`${metrics.total_arrays || 0} transformer-fed arrays`}
        />
        <KpiCard
          label="DC/AC Ratio"
          value={`${(inverterUtilization || 0).toFixed(2)} x`}
          helper="Ideal range: 1.1x – 1.3x"
        />
        <KpiCard
          label="Signal Coverage"
          value={`${Math.round(signalCoverage * 100)}%`}
          helper={`${summary?.signal_matches?.length || 0} signals mapped`}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">Array contribution</div>
            <p className="text-sm text-slate-500">
              Derived from the module assignments in Step 2. Shows each array's share of total DC capacity.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {arrayBreakdown.length === 0 && <p className="text-sm text-slate-500">No arrays defined yet.</p>}
            {arrayBreakdown.map((arr) => {
              const share = totalDc ? (arr.dc / totalDc) * 100 : 0;
              return (
                <div key={arr.id}>
                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                    <span className="font-medium text-slate-800">{arr.name}</span>
                    <span>{arr.dc.toFixed(2)} kWp</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">Readiness checklist</div>
            <p className="text-sm text-slate-500">Use this quick checklist before running performance KPI reports.</p>
          </CardHeader>
          <CardContent>
            <ChecklistItem
              label="Equipment modeled"
              complete={(summary?.pvmodules?.length || 0) > 0 && (summary?.inverters?.length || 0) > 0}
              detail={`${summary?.pvmodules?.length || 0} module types, ${summary?.inverters?.length || 0} inverter types`}
            />
            <ChecklistItem
              label="Array linkage"
              complete={(summary?.array_inverters?.length || 0) > 0}
              detail={`${summary?.arrays?.length || 0} arrays with inverter links`}
            />
            <ChecklistItem
              label="Signal mapping"
              complete={(summary?.signal_matches?.length || 0) > 0}
              detail={`${summary?.signal_matches?.length || 0} telemetry signals`}
            />
            <ChecklistItem
              label="Module density"
              complete={modulesPerArray >= 1000}
              detail={`${Math.round(modulesPerArray)} modules per array`}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, helper }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
        <div className="text-sm text-slate-500">{helper}</div>
      </CardContent>
    </Card>
  );
}

function ChecklistItem({ label, complete, detail }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div
        className={`h-5 w-5 rounded-full flex items-center justify-center text-xs font-semibold ${
          complete ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"
        }`}
      >
        {complete ? "✓" : "–"}
      </div>
      <div>
        <div className="font-medium text-slate-800">{label}</div>
        <div className="text-sm text-slate-500">{detail}</div>
      </div>
    </div>
  );
}
